import React, { useContext, useState } from 'react';
import { FormControlLabel, Grid, Radio, RadioGroup, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  RecentActors as RecentActorsIcon,
} from '@mui/icons-material';
import { VisaStatus } from 'API';
import CardBase from 'components/CardBase';
import IconCircle from 'components/IconCircle';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import { DICTIONARY } from 'shared/constants';
import { resolvers } from 'shared/enums';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';

const visaResolver = resolvers.VisaStatus;

const getProcessTypeStyles = (theme: Theme) => ({
  card: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: '150px',
    padding: '0.5rem',
    textAlign: 'center',
    margin: '0 auto',
  },
  field: {
    display: 'flex',
    flexGrow: 1,
    marginTop: theme.spacing(1),
    justifyContent: 'space-between',
  },
  margin: {
    margin: `${theme.spacing(2)} 0`,
  },
  radio: {
    alignItems: 'end',
    alignSelf: 'center',
    display: 'flex',
  },
  showMore: {
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'block',
    marginTop: '1rem',
    textDecoration: 'underline',
  },
});

interface Props {
  hideHeader?: boolean;
  processOptions?: Array<string>;
}

const ProcessType: React.FC<Props> = ({ hideHeader, processOptions }: Props) => {
  const theme = useTheme();
  const styles = getProcessTypeStyles(theme);
  const { process, setProcess } = useContext(CreateProcessContext);

  const [showMore, setShowMore] = useState(false);

  const handleProcessChange = (e:any) => {
    const { value } = e.target;
    setProcess((draft) => {
      draft.requestedProcess = value;

      // GlobalProcessFields unmounts when the process type changes, but anything
      // already entered there stays on the draft and would be saved against —
      // and displayed on — a non-global request. Clear it with the switch.
      if (value !== VisaStatus.GLOBAL_PROCESS) {
        draft.globalProcessDuration = null;
        draft.globalProcessCountryCode = null;
        draft.globalVisaType = null;
        draft.vendorID = null;
      }
    });
  };

  const options = processOptions || [...Object.keys(VisaStatus)];

  return (
    <>
      {!hideHeader && (
        <IconRow>
          <IconRowCell type="icon">
            <IconCircle Icon={RecentActorsIcon} />
          </IconRowCell>
          <IconRowCell type="text">
            <Typography variant="h6">{DICTIONARY.ProcessType}</Typography>
          </IconRowCell>
        </IconRow>
      )}
      <Grid container alignItems="center" sx={styles.margin}>
        <Grid size={12}>
          <RadioGroup
            aria-label="process"
            name="process-picker"
            value={process.requestedProcess || null}
            onChange={handleProcessChange}
            row
          >
            <Grid container spacing={2}>
              {options
                .filter((option, index) => (showMore ? true : index < 3))
                .map((option) => (
                  <Grid
                    key={option}
                    size={4}
                  >
                    <CardBase sx={styles.card}>
                      <RecentActorsIcon />
                      <FormControlLabel
                        sx={styles.field}
                        value={option}
                        control={(
                          <Radio
                            color="primary"
                            sx={styles.radio}
                          />
                        )}
                        labelPlacement="top"
                        label={visaResolver(option as VisaStatus)}
                      />
                    </CardBase>
                  </Grid>
                ))}
            </Grid>
          </RadioGroup>
          {options.length > 3 && (
            <Typography
              onClick={() => setShowMore(!showMore)}
              variant="caption"
              color="primary"
              align="center"
              sx={styles.showMore}
            >
              {showMore ? 'SHOW FEWER OPTIONS' : 'SHOW MORE OPTIONS'}
            </Typography>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ProcessType;
